import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  EMPTY,
  exhaustMap,
  Observable,
  of,
  Subject,
  timer,
  ignoreElements,
  from,
  scheduled,
  asyncScheduler,
} from 'rxjs';
import {
  catchError,
  finalize,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
  concatMap,
  withLatestFrom,
} from 'rxjs/operators';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { BaseRolesService } from '../../core/services/base-roles.service';
import { BaseTokenService } from '../../core/services/base-token.service';
import { BaseAuthenticationService } from '../../modules/auth/base-auth.service';
import { BaseToastAlertClasses } from '../../shared/enums/base-toast-alert-classes.enum';
import { baseAuthActions, baseFormsActions, baseSharedActions, baseActions } from '../actions';
import { baseAuthSelectors } from '../selectors';
import { BaseCoreState } from '../reducers';
import { BaseLocalStorageService } from '../../core/services/base-local-storage.service';
import { BaseDeviceService } from '../../shared/services/base-device.service';
import { BASE_ROLES } from '../../core/constants/base-roles';
import { BaseHttpErrorService } from '../../core/services/base-http-error.service';
import { SYSTEM_ALERT_REGISTER_TIME } from '../../core/constants/base-time.const';
import { AuthenticationSuccessfulResponse, UserRegisterCheckResponse } from '../../../api';
import { BaseModalService } from '../../shared/services/base-modal.service';
import { BaseCustomizedUserParamEnum } from '../../shared/enums/base-customized-user-param.enum';
import { BaseAppRoutes } from '../../shared/constants/base-app-routes';

@Injectable()
export class BaseAuthEffects {
  private stopRefresh$ = new Subject<void>();

  setUserInfoFromToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAuthActions.setUserInfoFromToken),
      map(() => baseAuthActions.successUserInfo()),
      catchError((response) => of(baseAuthActions.errorUserInfo({ error: response })))
    )
  );

  loadToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAuthActions.loadToken),
      switchMap((action) =>
        this.handleTokenLoad(
          () => this.authenticationService.login(action.payload),
          (response) => of(baseAuthActions.errorUserInfo({ error: response }))
        )
      )
    )
  );

  loadTokenFromMagicLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAuthActions.loadTokenFromMagicLink),
      switchMap((action) =>
        this.handleTokenLoad(
          () => this.authenticationService.getAuthTokensFromMagicLink(action.payload),
          (response) => {
            this.router.navigateByUrl('/401');
            return of(baseAuthActions.errorUserInfo({ error: response }));
          }
        )
      )
    )
  );

  refreshUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAuthActions.refreshUser),
      exhaustMap(() => {
        const refreshToken = this.tokenService.getRefreshToken();
        if (!refreshToken) {
          return of(baseAuthActions.errorRefreshUser(), baseAuthActions.logout());
        }

        return this.authenticationService.refresh(refreshToken).pipe(
          tap((response) => {
            this.tokenService.setTokens(response);
            const decodedToken = this.tokenService.decodeToken(response.token);
            this.rolesService.setRolesAndPermissions(decodedToken.roles, decodedToken.permissions);
            this.rolesService.setPermissions(decodedToken.permissions);
            this.store.dispatch(
              baseAuthActions.setFirstLoginIndicator({ payload: !this.localStorageService.get('login-id') })
            );
          }),
          mergeMap((response) => {
            const decodedToken = this.tokenService.decodeToken(response.token);
            return [
              baseAuthActions.setUserInfoFromToken({ payload: decodedToken }),
              baseAuthActions.successRefreshUser(),
            ];
          }),
          catchError(() => {
            return [baseAuthActions.errorRefreshUser(), baseAuthActions.logout()];
          })
        );
      })
    )
  );

  startRefreshTimer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAuthActions.successRefreshUser),
      tap(() => {
        this.stopRefresh$.next();
      }),
      switchMap(() => {
        const token = this.tokenService.getToken();
        if (!token) return EMPTY;

        const decodedToken = this.tokenService.decodeToken(token);
        const expirationTime = decodedToken.exp * 1000;
        const timeout = expirationTime - Date.now() - 60 * 1000;
        const MINIMUM_TIMER_THRESHOLD = 1000;

        if (timeout <= MINIMUM_TIMER_THRESHOLD) {
          return of(baseAuthActions.refreshUser());
        }

        return timer(timeout).pipe(
          takeUntil(this.stopRefresh$),
          map(() => baseAuthActions.refreshUser())
        );
      })
    )
  );

  stopRefreshTimer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(baseAuthActions.errorRefreshUser, baseAuthActions.logout),
        tap(() => this.stopRefresh$.next())
      ),
    { dispatch: false }
  );

  resetPasswordEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAuthActions.resetPasswordEmail),
      switchMap((action) => {
        return this.authenticationService.resetPasswordEmail(action.payload).pipe(
          mergeMap(() => [
            baseAuthActions.successResetPasswordEmail(),
            baseSharedActions.addSystemAlert({
              payload: {
                body: 'auth.password.resetPassword.success',
                class: BaseToastAlertClasses.success,
              },
            }),
          ]),
          catchError(() => of(baseAuthActions.errorResetPasswordEmail()))
        );
      })
    )
  );

  resetPasswordConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAuthActions.resetPasswordConfirmation),
      switchMap((action) => {
        return this.authenticationService.resetPassword(action.payload.body, action.payload.token).pipe(
          tap(() => {
            this.store.dispatch(baseAuthActions.navigateToLoginPage({}));
          }),
          mergeMap(() => [
            baseAuthActions.successResetPasswordConfirmation(),
            baseSharedActions.addSystemAlert({
              payload: {
                body: 'auth.password.resetPassword.success',
                class: BaseToastAlertClasses.success,
              },
            }),
          ]),
          catchError(() => of(baseAuthActions.errorResetPasswordConfirmation()))
        );
      })
    )
  );

  registerUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAuthActions.registerUser),
      switchMap((action) => {
        return this.authenticationService.registerUser(action.payload).pipe(
          tap(() => {
            this.store.dispatch(baseAuthActions.navigateToLoginPage({}));
            this.store.dispatch(baseFormsActions.clearAccountRegisterForm());
          }),
          map(() => baseAuthActions.successRegisterUser()),
          catchError(() => of(baseAuthActions.errorRegisterUser()))
        );
      })
    )
  );

  registerPSP$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAuthActions.registerPSP),
      switchMap((action) => {
        return this.authenticationService.registerPSP(action.payload).pipe(
          tap(() => {
            this.store.dispatch(baseAuthActions.navigateToLoginPage({}));
            this.store.dispatch(baseFormsActions.clearAccountRegisterForm());
          }),
          mergeMap(() => [
            baseAuthActions.successRegisterPSP(),
            baseSharedActions.addSystemAlert({
              payload: {
                body: 'auth.registerOrg.successMessage',
                class: BaseToastAlertClasses.success,
                duration: SYSTEM_ALERT_REGISTER_TIME,
              },
            }),
          ]),
          catchError(() => of(baseAuthActions.errorRegisterPSP()))
        );
      })
    )
  );

  registerMedicalCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAuthActions.registerCustomer),
      switchMap((action) => {
        return this.authenticationService.registerCustomer(action.payload).pipe(
          tap(() => {
            this.store.dispatch(baseAuthActions.navigateToLoginPage({}));
            this.store.dispatch(baseFormsActions.clearAccountRegisterForm());
          }),
          mergeMap(() => [
            baseAuthActions.successRegisterCustomer(),
            baseSharedActions.addSystemAlert({
              payload: {
                body: 'auth.registerOrg.successMessage',
                class: BaseToastAlertClasses.success,
                duration: SYSTEM_ALERT_REGISTER_TIME,
              },
            }),
          ]),
          catchError(() => of(baseAuthActions.errorRegisterCustomer()))
        );
      })
    )
  );

  registerBusinessCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAuthActions.registerBusinessCustomer),
      switchMap((action) => {
        return this.authenticationService.registerBusinessCustomer(action.payload).pipe(
          tap(() => {
            this.store.dispatch(baseAuthActions.navigateToLoginPage({}));
          }),
          mergeMap(() => [
            baseAuthActions.successRegisterBusinessCustomer(),
            baseSharedActions.addSystemAlert({
              payload: {
                body: 'auth.registerOrg.successMessage',
                class: BaseToastAlertClasses.success,
                duration: SYSTEM_ALERT_REGISTER_TIME,
              },
            }),
          ]),
          catchError(() => of(baseAuthActions.errorRegisterBusinessCustomer()))
        );
      })
    )
  );

  verifyRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAuthActions.verifyRegistration),
      switchMap((action) => {
        return this.authenticationService.verify(action.payload.id, action.payload.token, action.payload.type).pipe(
          tap(() => {
            this.store.dispatch(baseAuthActions.navigateToLoginPage({}));
          }),
          mergeMap(() => [
            baseAuthActions.verifyRegistrationSuccess(),
            baseSharedActions.addSystemAlert({
              payload: {
                body: 'auth.registered.success',
                class: BaseToastAlertClasses.success,
              },
            }),
          ]),
          catchError(() => {
            this.store.dispatch(baseAuthActions.navigateToLoginPage({}));
            return of(baseAuthActions.verifyRegistrationError());
          })
        );
      })
    )
  );

  goToLoginPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(baseAuthActions.navigateToLoginPage),
        withLatestFrom(this.store.select(baseAuthSelectors.getCustomizedUserStatus).pipe(map(Boolean))),
        switchMap(([{ fromLogout }, isCustomizedUser]) =>
          from(
            this.router.navigate([BaseAppRoutes.login], {
              replaceUrl: true,
              state: { fromLogout: !!fromLogout },
              queryParams: {
                network: isCustomizedUser ? BaseCustomizedUserParamEnum.emasters : undefined,
              },
            })
          ).pipe(
            tap((ok) => {
              if (ok && fromLogout && window.history.state?.fromLogout) {
                this.store.dispatch(baseActions.resetStore());
                this.clearFromLogout();
              }
            }),
            ignoreElements()
          )
        )
      ),
    { dispatch: false }
  );

  logoutUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAuthActions.logout),
      exhaustMap(() => {
        const refreshToken = this.tokenService.getRefreshToken();

        if (!refreshToken) {
          this.cleanupAfterLogout();
          return of(baseAuthActions.navigateToLoginPage({ fromLogout: true }));
        }

        return this.authenticationService.logout(refreshToken).pipe(
          map(() => baseAuthActions.logoutSuccess()),
          catchError(() => of(baseAuthActions.logoutError())),
          concatMap((action) =>
            scheduled([action, baseAuthActions.navigateToLoginPage({ fromLogout: true })], asyncScheduler)
          ),
          finalize(() => this.cleanupAfterLogout())
        );
      })
    )
  );

  loadRegistrationCheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAuthActions.loadRegistrationCheck),
      mergeMap((action) =>
        this.authenticationService.validateInviteLink(action.payload).pipe(
          map((response: UserRegisterCheckResponse) =>
            baseAuthActions.successLoadRegistrationCheck({ payload: response })
          ),
          catchError(() => of(baseAuthActions.errorLoadRegistrationCheck()))
        )
      )
    )
  );

  private handleTokenLoad(
    authCall: () => Observable<AuthenticationSuccessfulResponse>,
    onError: (response: any) => Observable<any>
  ) {
    return authCall().pipe(
      tap((response) => {
        this.tokenService.setTokens(response);
        const decodedToken = this.tokenService.decodeToken(response.token);
        this.rolesService.setRolesAndPermissions(decodedToken.roles, decodedToken.permissions);
        this.store.dispatch(
          baseAuthActions.setFirstLoginIndicator({ payload: !this.localStorageService.get('login-id') })
        );
        if (
          !this.deviceService.isDesktop() &&
          !this.localStorageService.get('login-id') &&
          !this.rolesService.hasRole(BASE_ROLES.CUSTOMER_ADMIN)
        ) {
          this.deviceService.openMobileDeviceInfoModal();
        }
        this.localStorageService.set('login-id', uuidv4());

        this.doRedirect();
      }),
      mergeMap((response) => {
        const userInfo = this.tokenService.decodeToken(response.token);
        return [
          baseAuthActions.setUserInfoFromToken({ payload: userInfo }),
          baseSharedActions.addSystemAlert({
            payload: {
              body: 'auth.loginSuccess',
              class: BaseToastAlertClasses.success,
            },
          }),
          baseAuthActions.successRefreshUser(),
        ];
      }),
      catchError(onError)
    );
  }

  private doRedirect() {
    const returnUrl = this.route.snapshot.queryParams['redirectUrl'];

    if (returnUrl) {
      this.router.navigateByUrl(returnUrl, { replaceUrl: true });
      return;
    }

    this.router.navigate([this.rolesService.hasWorkCouncilRole() ? '/offers' : '/dashboard'], { replaceUrl: true });
  }

  private clearFromLogout(): void {
    const current = window.history.state;
    if (current?.fromLogout) {
      const { fromLogout, ...rest } = current;
      window.history.replaceState(rest, '', window.location.href);
    }
    const nav = this.router.getCurrentNavigation?.();
    if (nav?.extras?.state?.['fromLogout']) {
      delete nav.extras.state['fromLogout'];
    }
  }

  private cleanupAfterLogout() {
    this.tokenService.deleteTokens();
    this.rolesService.clearRolesAndPermissions();
    this.modalService.closeAllModals();
    if (this.offCanvasService.hasOpenOffcanvas()) {
      this.offCanvasService.dismiss();
    }
  }

  constructor(
    private actions$: Actions,
    private store: Store<BaseCoreState>,
    private authenticationService: BaseAuthenticationService,
    private tokenService: BaseTokenService,
    private rolesService: BaseRolesService,
    private router: Router,
    private localStorageService: BaseLocalStorageService,
    private deviceService: BaseDeviceService,
    private baseHttpErrorService: BaseHttpErrorService,
    private modalService: BaseModalService,
    private offCanvasService: NgbOffcanvas,
    private route: ActivatedRoute
  ) {}
}
